import { useMemo, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon, GoalIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { LanguageFlag } from '@/components/atoms/FlagEmoji/LanguageFlag';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  getCampaignStatusLabelKey,
  getCampaignStatusVariant,
} from '@/modules/campaigns/components/analytics/campaignStatusBadge';
import { getChannelIcon } from '@/modules/campaigns/components/Tables/CampaignsTable/ChannelCell';
import {
  IdentityRow,
  InlineCopyButton,
  isKnownChannel,
} from '@/modules/contacts/components/IdentityRow';
import { useContactCampaigns } from '@/modules/contacts/hooks/useContactCampaigns';
import { useContactIdentities } from '@/modules/contacts/hooks/useContactIdentities';
import { selectAccountSlug } from '@/redux/session/selectors';
import type { Contact, ContactCampaignSubscriber, ContactIdentity } from '@/shared/types/contacts';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { timeFromNow } from '@/util/dateUtils';
import { formatLanguageLabel } from '@/util/languageOptions';

interface ProfileSidebarProps {
  contact: Contact;
  nameLinkTo?: string;
  footer?: React.ReactNode;
  bordered?: boolean;
}

const enrollmentLinkPath = (
  slug: string,
  contact: Contact,
  subscriber: ContactCampaignSubscriber
) => {
  const base = `/${slug}/campaigns/${subscriber.campaign.campaign_id}/logs`;
  if (subscriber.campaign.status?.toLowerCase() === 'draft') {
    return base;
  }
  if (subscriber.campaign.type === 'payment_recovery') {
    return `${base}/${subscriber.subscriber_id}`;
  }
  const q = contact.email ?? contact.phone;
  return q ? `${base}?q=${encodeURIComponent(q)}` : base;
};

const conversationStatusKey = (
  subscriber: ContactCampaignSubscriber
): 'concluded' | 'in_progress' | 'no_reply' | 'not_initiated' => {
  if (subscriber.last_replied_at) {
    return 'concluded';
  }
  const reachedOut =
    !!subscriber.last_sent_at || !!subscriber.last_delivered_at || !!subscriber.last_read_at;
  const campaignStatus = subscriber.campaign.status?.toLowerCase();
  const campaignTerminal = campaignStatus === 'canceled' || campaignStatus === 'failed';
  if (reachedOut && campaignTerminal) {
    return 'no_reply';
  }
  if (reachedOut) {
    return 'in_progress';
  }
  return 'not_initiated';
};

const pickRecentChannel = (identities: ContactIdentity[]): string | undefined => {
  const withChannel = identities
    .filter((i) => isKnownChannel(i.channel))
    .sort((a, b) => (b.updated || '').localeCompare(a.updated || ''));
  return withChannel[0]?.channel ?? undefined;
};

const LanguageValue = ({ code }: { code: string }) => {
  return (
    <span className="flex items-center gap-1.5">
      <LanguageFlag code={code} />
      <span>{formatLanguageLabel(code)}</span>
    </span>
  );
};

const ChannelValue = ({ channel }: { channel: string }) => {
  const { t } = useTranslation();
  const label = t(getChannelKey(channel as IntegrationType));
  return (
    <span className="flex items-center gap-1.5">
      {getChannelIcon(channel, 14)}
      <span>{label}</span>
    </span>
  );
};

const DetailRow = ({ label, value }: { label: string; value?: React.ReactNode }) => {
  if (value === undefined || value === null) {
    return null;
  }
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="shrink-0 text-sm text-foreground-muted">{label}</span>
      <span className="min-w-0 truncate text-sm text-foreground">{value}</span>
    </div>
  );
};

const CampaignRow = ({
  subscriber,
  to,
}: {
  subscriber: ContactCampaignSubscriber;
  to: string | null;
}) => {
  const { t } = useTranslation();
  const status = subscriber.campaign.status;
  const flavour = getCampaignStatusVariant(status);
  const typeLabelKey =
    subscriber.campaign.type === 'payment_recovery'
      ? 'campaigns.setup.type_payment_recovery'
      : 'campaigns.setup.type_outreach';
  const launchedAgo = timeFromNow(subscriber.campaign.created);
  const conversationKey = conversationStatusKey(subscriber);
  const getConversationLine = () => {
    if (conversationKey === 'concluded') {
      return t('contacts.campaign.conversation_concluded', {
        0: timeFromNow(subscriber.last_replied_at ?? subscriber.updated),
      });
    }
    if (conversationKey === 'in_progress') {
      return t('contacts.campaign.conversation_in_progress');
    }
    if (conversationKey === 'no_reply') {
      return t('contacts.campaign.conversation_no_reply');
    }
    return t('contacts.campaign.conversation_not_initiated');
  };
  const conversationLine = getConversationLine();

  const card = (
    <div className="group flex flex-col gap-1 rounded-md px-2 py-2 transition-colors hover:bg-bg-muted-50">
      <div className="flex items-start gap-2">
        <GoalIcon size={14} className="mt-0.5 shrink-0 text-foreground-muted" />
        <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1 text-sm font-medium text-foreground">
            <span className="truncate">{subscriber.campaign.name}</span>
            {to && (
              <ExternalLinkIcon
                size={12}
                className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
              />
            )}
          </span>
          {status && (
            <Badge type="secondary" flavour={flavour} rounded={false}>
              {t(getCampaignStatusLabelKey(status))}
            </Badge>
          )}
        </div>
      </div>
      <div className="pl-6 text-xs text-foreground-muted">
        {t(typeLabelKey)} · {t('contacts.campaign.launched_ago', { 0: launchedAgo })}
      </div>
      <div className="pl-6 text-xs text-foreground-muted">{conversationLine}</div>
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="block">
        {card}
      </Link>
    );
  }
  return card;
};

export const ProfileSidebar = ({
  contact,
  nameLinkTo,
  footer,
  bordered = true,
}: ProfileSidebarProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { identities } = useContactIdentities(contact.contact_id);
  const { subscribers: campaignSubscribers } = useContactCampaigns(contact.contact_id);
  const [showMore, setShowMore] = useState(false);

  const recentChannel = useMemo(() => pickRecentChannel(identities), [identities]);

  return (
    <div className={cn(bordered && 'overflow-hidden rounded-lg border border-stroke')}>
      <div className={cn(bordered && 'px-5 pt-4 pb-5')}>
        {/* Details — first 5 visible, the rest collapsed behind Show more */}
        {(() => {
          const allRows: { key: string; label: string; value: React.ReactNode }[] = [
            contact.last_replied && {
              key: 'last_replied',
              label: t('contacts.last_replied'),
              value: timeFromNow(contact.last_replied),
            },
            contact.last_contacted && {
              key: 'last_contacted',
              label: t('contacts.last_contacted'),
              value: timeFromNow(contact.last_contacted),
            },
            recentChannel && {
              key: 'recent_channel',
              label: t('contacts.recent_channel'),
              value: <ChannelValue channel={recentChannel} />,
            },
            contact.language && {
              key: 'language',
              label: t('contacts.language'),
              value: <LanguageValue code={contact.language} />,
            },
            // TODO: Communication / Consent row — pending contact_preferences schema (Figma 2438:19535)
            { key: 'created', label: t('common.created'), value: timeFromNow(contact.created) },
            { key: 'updated', label: t('common.updated'), value: timeFromNow(contact.updated) },
            contact.last_seen && {
              key: 'last_seen',
              label: t('contacts.last_seen'),
              value: timeFromNow(contact.last_seen),
            },
            {
              key: 'moveo_id',
              label: t('contacts.moveo_id'),
              value: (
                <span className="flex items-center gap-1">
                  <span className="font-mono text-xs">{contact.contact_id.slice(0, 12)}</span>
                  <InlineCopyButton value={contact.contact_id} />
                </span>
              ),
            },
          ].filter(Boolean) as { key: string; label: string; value: React.ReactNode }[];

          const visibleRows = showMore ? allRows : allRows.slice(0, 5);
          const hasMore = allRows.length > 5;

          return (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">
                {t('contacts.recent_activity')}
              </h3>
              {visibleRows.map((row) => (
                <DetailRow key={row.key} label={row.label} value={row.value} />
              ))}
              {hasMore && (
                <button
                  type="button"
                  onClick={() => setShowMore((v) => !v)}
                  className="mt-1 flex items-center gap-2 text-sm font-medium text-surface-button-primary hover:underline"
                >
                  {showMore ? t('contacts.show_less') : t('contacts.show_more')}
                  {showMore ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
                </button>
              )}
              {nameLinkTo && (
                <Link
                  to={nameLinkTo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-informational hover:underline"
                >
                  {t('contacts.view_full_profile')}
                  <ExternalLinkIcon size={14} />
                </Link>
              )}
            </div>
          );
        })()}

        {/* Custom fields */}
        {contact.custom_fields && Object.keys(contact.custom_fields).length > 0 && (
          <>
            <h3 className="mt-4 mb-2 text-sm font-semibold text-foreground">
              {t('contacts.custom_fields')}
            </h3>
            {Object.entries(contact.custom_fields).map(([key, value]) => (
              <DetailRow key={key} label={key} value={String(value ?? '')} />
            ))}
          </>
        )}

        {/* Contact information */}
        {identities.length > 0 && (
          <>
            <h3 className="mt-4 mb-2 text-sm font-semibold text-foreground">
              {t('contacts.contact_information')}
            </h3>
            <div className="-mx-2">
              {identities.map((identity) => (
                <IdentityRow key={identity.identity_id} identity={identity} />
              ))}
            </div>
          </>
        )}

        {/* Campaigns */}
        {campaignSubscribers.length > 0 && (
          <>
            <div className="mt-4 mb-2 flex items-center gap-1">
              <h3 className="text-sm font-semibold text-foreground">{t('contacts.campaigns')}</h3>
              <InfoTooltip content={t('contacts.enrollment_tooltip')} size="large">
                <InfoIcon size={12} className="text-foreground-muted" />
              </InfoTooltip>
            </div>
            <div className="-mx-2 flex flex-col gap-1">
              {campaignSubscribers.map((subscriber) => (
                <CampaignRow
                  key={subscriber.subscriber_id}
                  subscriber={subscriber}
                  to={slug ? enrollmentLinkPath(slug, contact, subscriber) : null}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {footer && <div className={cn('mt-4', bordered && 'px-5 pb-5')}>{footer}</div>}
    </div>
  );
};
